import React, { type FC, useState, useRef, useEffect } from 'react'
import './index.css'

const generateTicks = (
  maxValue: number,
): { major: number[]; medium: number[]; small: number[] } => {
  const ticks: { major: number[]; medium: number[]; small: number[] } = {
    major: [],
    medium: [],
    small: [],
  }

  const step = maxValue / 20

  for (let i = 0; i <= 20; i++) {
    const value = Math.round(i * step)

    if (i % 4 === 0) {
      ticks.major.push(value)
    } else if (i % 2 === 0) {
      ticks.medium.push(value)
    } else {
      ticks.small.push(value)
    }
  }

  return ticks
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

interface CustomReferenceDotProps {
  x: number
  y: number
  color: string
  label: string
  value: number
  additionalLabel?: string
  additionalValue?: string
  chartWidth?: number
  marginLeft?: number
  marginRight?: number
}

const CustomReferenceDot: FC<CustomReferenceDotProps> = ({
  x,
  y,
  color,
  label,
  value,
  additionalLabel,
  additionalValue,
  chartWidth = 1100,
  marginLeft = 12,
  marginRight = 12,
}) => {
  const [showTooltip, setShowTooltip] = useState(false)

  // Draw marker taller above the baseline but keep everything inside the SVG
  const lineHeight = 26
  const shapeWidth = 18
  const shapeHeight = 18

  const lineYStart = y
  const lineYEnd = y - lineHeight
  const shapeCenterY = lineYEnd - shapeHeight / 2

  // Calculate tooltip positioning to avoid cut-off
  const tooltipWidth = 200
  const tooltipHeight = 50
  const tooltipPaddingLeft = 10 // Space between marker and tooltip when on left side
  const tooltipPaddingRight = 10 // More space between marker and tooltip when on right side
  
  // Calculate available space on left and right
  const availableLeft = x - marginLeft
  const availableRight = (chartWidth + marginLeft) - x
  
  // Determine tooltip x position
  let tooltipX: number
  let tooltipY: number
  if (availableLeft < tooltipWidth + tooltipPaddingLeft) {
    // Near left edge - position tooltip to the right of marker with more distance
    tooltipX = x + tooltipPaddingRight
    tooltipY = shapeCenterY + 10
  } else if (availableRight < tooltipWidth + tooltipPaddingLeft) {
    // Near right edge - position tooltip to the left of marker
    tooltipX = x - tooltipWidth - tooltipPaddingLeft
    tooltipY = shapeCenterY + 10
  } else {
    // Center tooltip on marker - position lower
    tooltipX = x - tooltipWidth / 2
    tooltipY = shapeCenterY + 20
  }
  
  // Ensure tooltip doesn't go outside chart bounds
  tooltipX = Math.max(marginLeft, Math.min(tooltipX, chartWidth + marginLeft - tooltipWidth))

  return (
    <g
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      style={{ cursor: 'pointer' }}
    >
      <line
        x1={x}
        y1={lineYStart}
        x2={x}
        y2={lineYEnd}
        stroke="rgba(45, 49, 66)"
        strokeWidth={1}
      />
      <svg
        x={x - shapeWidth / 2}
        y={shapeCenterY - shapeHeight / 2}
        width={shapeWidth}
        height={shapeHeight}
        viewBox="0 0 9 9"
        xmlns="http://www.w3.org/2000/svg"
      >
        {React.createElement('path', {
          d: 'M8.41824 1.46946C8.41824 1.11356 8.13455 0.824707 7.77348 0.824707H1.32591C0.971293 0.824707 0.681152 1.11356 0.681152 1.46946V2.96946H8.41824V1.46946Z',
          fill: color,
        })}
        {React.createElement('path', {
          d: 'M8.41824 2.96946V4.42632C8.41824 4.59718 8.35374 4.76095 8.23124 4.88216L5.00745 8.10595C4.75599 8.35741 4.34985 8.35741 4.0984 8.10595L0.874611 4.88216C0.752107 4.76095 0.681152 4.59718 0.681152 4.42632V2.96946H8.41824Z',
          fill: '#2A3442',
        })}
      </svg>
      {showTooltip && (
        <foreignObject x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight}>
          <div
            style={{
              backgroundColor: '#FFFFFF',
              color: '#000000',
              padding: '4px 6px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '400',
              border: '1px solid #E0E0E0',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              whiteSpace: 'nowrap',
              textAlign: 'left',
              fontFamily: "'Inter', sans-serif",
              pointerEvents: 'none',
            }}
          >
            {additionalLabel && additionalValue ? (
              <>
                {label}: <strong>{new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(value)}</strong>
                <br />
                {additionalLabel}: <strong>{new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(parseFloat(additionalValue))}</strong>
              </>
            ) : (
              <>
                {label}: <strong>{new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(value)}</strong>
              </>
            )}
          </div>
        </foreignObject>
      )}
    </g>
  )
}

interface BarTooltipProps {
  barX: number
  barY: number
  barWidth: number
  barHeight: number
  label: string
  value: number
  chartWidth: number
  marginLeft: number
  marginRight: number
  show: boolean
  mouseX?: number
  mouseY?: number
}

const BarTooltip: FC<BarTooltipProps> = ({
  barX,
  barY,
  barWidth,
  barHeight,
  label,
  value,
  chartWidth,
  marginLeft,
  marginRight,
  show,
  mouseX,
  mouseY,
}) => {
  if (!show) return null

  const tooltipWidth = 200
  const tooltipHeight = 50
  const padding = 10

  // Use mouse X position if available, otherwise center on bar
  const centerX = mouseX !== undefined ? mouseX : barX + barWidth / 2

  let tooltipX = centerX - tooltipWidth / 2

  const minX = marginLeft
  const maxX = chartWidth + marginLeft - tooltipWidth

  tooltipX = Math.max(minX, Math.min(tooltipX, maxX))

  // Position tooltip above the cursor (or bar if no mouse position)
  let tooltipY = (mouseY !== undefined ? mouseY : barY) - tooltipHeight - padding
  
  // Ensure tooltip doesn't go above the SVG top edge
  const minY = 0
  tooltipY = Math.max(minY, tooltipY)

  const formattedValue = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)

  return (
    <foreignObject x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight}>
      <div
        style={{
          backgroundColor: '#FFFFFF',
          color: '#000000',
          padding: '4px 6px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '400',
          border: '1px solid #E0E0E0',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          whiteSpace: 'nowrap',
          textAlign: 'left',
          fontFamily: "'Inter', sans-serif",
          pointerEvents: 'none',
        }}
      >
        {label}: <strong>{formattedValue}</strong>
      </div>
    </foreignObject>
  )
}

interface CustomXAxisTicksProps {
  x: number
  y: number
  value: number
  ticks: { major: number[]; medium: number[]; small: number[] }
  chartLabel: string
}

const CustomXAxisTicks: FC<CustomXAxisTicksProps> = ({ x, y, value, ticks, chartLabel }) => {
  const isMajorTick = ticks.major.includes(value)
  const isMiddleTick = ticks.medium.includes(value)
  const tickThickness = 1
  const isTheLastMajorTick = value === ticks.major[ticks.major.length - 1]
  const tickColor = '#BFC4CC'

  const labelXOffset = isTheLastMajorTick ? -2 : 5
  const labelAnchor = isTheLastMajorTick ? 'end' : 'start'

  return (
    <g transform={`translate(${x},${y})`}>
      {isMajorTick && (
        <>
          <line
            x1={0}
            y1={-4}
            x2={0}
            y2={0}
            stroke={tickColor}
            strokeWidth={tickThickness}
          />
          <text
            x={labelXOffset}
            y={-6}
            textAnchor={labelAnchor}
            fill={'#4C5666'}
            fontSize="10px"
          >
            {isTheLastMajorTick ? (
              <>
                {'Charge'}: <tspan fontWeight="bold">{formatCurrency(value)}</tspan>
              </>
            ) : (
              formatCurrency(value)
            )}
          </text>
        </>
      )}
      {isMiddleTick && !isMajorTick && (
        <line
          x1={0}
          y1={-8}
          x2={0}
          y2={0}
          stroke={tickColor}
          strokeWidth={tickThickness}
        />
      )}
      {!isMajorTick && !isMiddleTick && (
        <line
          x1={0}
          y1={-4}
          x2={0}
          y2={0}
          stroke={tickColor}
          strokeWidth={tickThickness}
        />
      )}
    </g>
  )
}

const renderCustomLegend = ({
  name,
  charge,
  payerPaid,
  patientPaid,
  patientPending,
  payerAdjusted,
  contractValue,
  payerAllowed,
  adjustments,
}: {
  name: string
  charge: number
  payerPaid: number
  patientPaid: number
  patientPending: number
  payerAdjusted: number
  contractValue: number | null
  payerAllowed: number | null
  adjustments: number
}) => {
  const format = (v: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v)

  const legendData = [
    { label: name, value: format(charge) },
    {
      label: 'Payer Paid',
      value: payerPaid ? format(payerPaid) : undefined,
      color: '#2E6BE6',
    },
    {
      label: 'Patient Paid',
      value: patientPaid ? format(patientPaid) : undefined,
      color: '#4AB08C',
    },
    {
      label: 'Patient Pending',
      value: patientPending ? format(patientPending) : undefined,
      color: '#9FE3C9',
    },
    {
      label: 'Adjustments',
      value: adjustments ? format(adjustments) : undefined,
      color: '#DDE0E6',
    },
    {
      label: 'Contract Value',
      value: contractValue != null ? format(contractValue) : undefined,
      color: '#2E6BE6',
      darkColor: '#2A3442',
    },
    {
      label: 'Payer Allowed',
      value:
        payerAllowed && payerAllowed < (contractValue || 0) && payerAllowed
          ? format(payerAllowed || 0)
          : undefined,
      color: '#E57324',
      darkColor: '#2A3442',
    },
    {
      label: 'Payer Adjusted',
      value:
        payerAllowed && payerAllowed < (contractValue || 0) && payerAllowed
          ? format(payerAdjusted)
          : undefined,
      color: '#F25D66A6',
    },
  ]
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '25px'
      }}
    >
      {legendData
        .filter((i) => i.value)
        .map((item, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {item.darkColor ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 9 9"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: '5px' }}
              >
                {React.createElement('path', {
                  d: 'M8.41824 1.46946C8.41824 1.11356 8.13455 0.824707 7.77348 0.824707H1.32591C0.971293 0.824707 0.681152 1.11356 0.681152 1.46946V4.42632C0.681152 4.59718 0.752107 4.76095 0.874611 4.88216L4.0984 8.10595C4.34985 8.35741 4.75599 8.35741 5.00745 8.10595L8.23124 4.88216C8.35374 4.76095 8.41824 4.59718 8.41824 4.42632V1.46946Z',
                  fill: item.darkColor,
                })}
                {React.createElement('path', {
                  d: 'M8.41824 1.46946C8.41824 1.11356 8.13455 0.824707 7.77348 0.824707H1.32591C0.971293 0.824707 0.681152 1.11356 0.681152 1.46946V2.5H8.41824V1.46946Z',
                  fill: item.color,
                })}
              </svg>
            ) : item.color !== undefined ? (
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  marginRight: '5px',
                }}
              ></div>
            ) : null}
            <span style={{ fontSize: '12px', color: '#4C5666' }}>
              {item.label}: {item.value}
            </span>
          </div>
        ))}
    </div>
  )
}

export interface ClaimChartData {
  name: string
  charge: number
  payerPaid: number
  patientPaid: number
  patientPending: number
  payerAdjusted: number
  contractValue: number | null
  payerAllowed: number | null
  adjustments: number
}

export interface HorizontalStackedBarChartProps {
  chartData: ClaimChartData
}

export const HorizontalStackedBarChart: FC<HorizontalStackedBarChartProps> = ({ chartData }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [dimensions, setDimensions] = useState({ width: 1100, height: 210 })
  const [hoveredBar, setHoveredBar] = useState<string | null>(null)
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null)

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        // Try to use the width of the OverviewLayoutRow (parent layout row), fallback to own width, then default
        const overviewRowElement = containerRef.current.closest?.('[data-overview-layout-row]') as HTMLElement | null
        const overviewRowRect = overviewRowElement?.getBoundingClientRect()
        const newWidth =
          overviewRowRect?.width && overviewRowRect.width > 0
            ? overviewRowRect.width
            : rect.width || 1100

        const newHeight = 210
        setDimensions({
          width: newWidth,
          height: newHeight,
        })
      } else {
        // Fallback if ref not ready
        setDimensions({ width: 1100, height: 210 })
      }
    }

    // Initial update with slight delay to ensure DOM is ready
    const timeoutId = setTimeout(updateDimensions, 0)
    window.addEventListener('resize', updateDimensions)
    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', updateDimensions)
    }
  }, [])

  // Ensure charge is at least 1 to avoid division by zero
  const maxCharge = Math.max(chartData.charge, 0)
  const ticks = generateTicks(maxCharge)
  const margin = { left: 12, right: 12, top: 50, bottom: 70 }
  const chartWidth = Math.max(dimensions.width - margin.left - margin.right, 0)
  const chartHeight = Math.max(dimensions.height - margin.top - margin.bottom, 0)
  const barHeight = Math.min(48, chartHeight)
  const barY = margin.top + chartHeight / 2 - barHeight / 2

  // Scale function to convert value to x position
  const scaleX = (value: number) => (value / maxCharge) * chartWidth

  // Calculate bar segment positions
  const payerPaidX = margin.left
  const payerPaidWidth = scaleX(chartData.payerPaid)
  const patientPaidX = payerPaidX + payerPaidWidth
  const patientPaidWidth = scaleX(chartData.patientPaid)
  const patientPendingX = patientPaidX + patientPaidWidth
  const patientPendingWidth = scaleX(chartData.patientPending)
  const adjustmentsX = patientPendingX + patientPendingWidth
  const adjustmentsWidth = scaleX(chartData.adjustments)

  const allTicks = [...ticks.major, ...ticks.medium, ...ticks.small].sort((a, b) => a - b)

  // Safety check - ensure we have valid dimensions
  if (!dimensions.width || !dimensions.height || chartWidth <= 0 || chartHeight <= 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Loading chart...
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        // Let content define height so markers and legend are never clipped
        borderRadius: '8px',
        position: 'relative',
        fontFamily: "'Inter', sans-serif",
        backgroundColor: '#ffffff',
      }}
    >
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        style={{ display: 'block' }}
      >
        {/* Background area - full charge layer */}
        <rect
          x={margin.left}
          y={margin.top}
          width={chartWidth}
          height={chartHeight}
          fill="#F0F3F6"
        />

        {/* Background area - stacked bars background */}
        <rect
          x={margin.left}
          y={barY}
          width={chartWidth}
          height={barHeight}
          fill="#F1F3F5"
        />

        {/* Reference lines */}
        {ticks.major.map((value, index) => {
          const x = margin.left + scaleX(value)
          return (
            <line
              key={value}
              x1={x}
              y1={margin.top}
              x2={x}
              y2={margin.top + chartHeight}
              stroke={index === 0 || index === 5 ? '#BFC4CC' : '#DDE0E6'}
              strokeWidth={1}
            />
          )
        })}

        {/* X-axis line */}
        <line
          x1={margin.left}
          y1={margin.top}
          x2={margin.left + chartWidth}
          y2={margin.top}
          stroke="#BFC4CC"
          strokeWidth={1}
        />

        {/* X-axis ticks */}
        {allTicks.map((value) => {
          const x = margin.left + scaleX(value)
          return (
            <CustomXAxisTicks
              key={value}
              x={x}
              y={margin.top}
              value={value}
              ticks={ticks}
              chartLabel={chartData.name}
            />
          )
        })}

        {/* Stacked bars */}
        {chartData.payerPaid > 0 && (
          <rect
            x={payerPaidX}
            y={barY}
            width={payerPaidWidth}
            height={barHeight}
            fill="#2E6BE6"
            onMouseEnter={() => setHoveredBar('payerPaid')}
            onMouseLeave={() => {
              setHoveredBar(null)
              setMousePos(null)
            }}
            onMouseMove={(e) => {
              if (svgRef.current) {
                const rect = svgRef.current.getBoundingClientRect()
                setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
              }
            }}
            style={{ cursor: 'pointer' }}
          />
        )}
        {chartData.patientPaid > 0 && (
          <rect
            x={patientPaidX}
            y={barY}
            width={patientPaidWidth}
            height={barHeight}
            fill="#4AB08C"
            onMouseEnter={() => setHoveredBar('patientPaid')}
            onMouseLeave={() => {
              setHoveredBar(null)
              setMousePos(null)
            }}
            onMouseMove={(e) => {
              if (svgRef.current) {
                const rect = svgRef.current.getBoundingClientRect()
                setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
              }
            }}
            style={{ cursor: 'pointer' }}
          />
        )}
        {chartData.patientPending > 0 && (
          <rect
            x={patientPendingX}
            y={barY}
            width={patientPendingWidth}
            height={barHeight}
            fill="#9FE3C9"
            onMouseEnter={() => setHoveredBar('patientPending')}
            onMouseLeave={() => {
              setHoveredBar(null)
              setMousePos(null)
            }}
            onMouseMove={(e) => {
              if (svgRef.current) {
                const rect = svgRef.current.getBoundingClientRect()
                setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
              }
            }}
            style={{ cursor: 'pointer' }}
          />
        )}
        {/* Remaining charge segment to fill the rest of the bar up to total Charge */}
        {chartData.charge > 0 && (
          (() => {
            const usedValue =
              chartData.payerPaid +
              chartData.patientPaid +
              chartData.patientPending

            const remainingValue = Math.max(0, chartData.charge - usedValue)

            if (remainingValue <= 0) {
              return null
            }

            const remainingX =
              margin.left +
              scaleX(chartData.payerPaid + chartData.patientPaid + chartData.patientPending)
            const remainingWidth = scaleX(remainingValue)

            return (
              <rect
                x={remainingX}
                y={barY}
                width={remainingWidth}
                height={barHeight}
                fill="#DAE0E7"
                onMouseEnter={() => setHoveredBar('adjustments')}
                onMouseLeave={() => {
                  setHoveredBar(null)
                  setMousePos(null)
                }}
                onMouseMove={(e) => {
                  if (svgRef.current) {
                    const rect = svgRef.current.getBoundingClientRect()
                    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
            )
          })()
        )}
        {/* Grey bar inside Payer Adjusted area (between Payer Allowed and Contract Value) */}
        {chartData.payerAllowed !== null &&
          chartData.contractValue !== null &&
          chartData.payerAllowed < chartData.contractValue && (
            <rect
              x={margin.left + scaleX(chartData.payerAllowed)}
              y={barY}
              width={scaleX(chartData.contractValue - chartData.payerAllowed)}
              height={barHeight}
              fill="#DAE0E7"
              onMouseEnter={() => setHoveredBar('payerAdjustedGrey')}
              onMouseLeave={() => {
                setHoveredBar(null)
                setMousePos(null)
              }}
              onMouseMove={(e) => {
                if (svgRef.current) {
                  const rect = svgRef.current.getBoundingClientRect()
                  setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
                }
              }}
              style={{ cursor: 'pointer' }}
            />
          )}
        {/* Payer adjusted area - overlay between Payer Allowed and Contract Value */}
        {chartData.payerAllowed !== null &&
          chartData.contractValue !== null &&
          chartData.payerAllowed < chartData.contractValue && (
            <rect
              x={margin.left + scaleX(chartData.payerAllowed)}
              y={margin.top}
              width={scaleX(chartData.contractValue - chartData.payerAllowed)}
              height={chartHeight}
              fill="#F25D66"
              fillOpacity={0.5}
              onMouseEnter={() => setHoveredBar('payerAdjustedOverlay')}
              onMouseLeave={() => {
                setHoveredBar(null)
                setMousePos(null)
              }}
              onMouseMove={(e) => {
                if (svgRef.current) {
                  const rect = svgRef.current.getBoundingClientRect()
                  setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
                }
              }}
              style={{ cursor: 'pointer' }}
            />
          )}

        {/* Contract Value reference line and dot */}
        {chartData.contractValue !== null && (
          <>
            <line
              x1={margin.left + scaleX(chartData.contractValue)}
              y1={margin.top}
              x2={margin.left + scaleX(chartData.contractValue)}
              y2={margin.top + chartHeight}
              stroke="rgba(45, 49, 66)"
              strokeWidth={1}
            />
            <CustomReferenceDot
              x={margin.left + scaleX(chartData.contractValue)}
              y={margin.top + 18}
              color="#2E6BE6"
              label="Contract Value"
              value={chartData.contractValue}
              chartWidth={chartWidth}
              marginLeft={margin.left}
              marginRight={margin.right}
            />
          </>
        )}

        {/* Payer Allowed reference line and dot */}
        {chartData.payerAllowed !== null &&
          chartData.contractValue !== null &&
          chartData.payerAllowed < chartData.contractValue && (
            <>
              <line
                x1={margin.left + scaleX(chartData.payerAllowed)}
                y1={margin.top}
                x2={margin.left + scaleX(chartData.payerAllowed)}
                y2={margin.top + chartHeight}
                stroke="rgba(45, 49, 66)"
                strokeWidth={1}
              />
              <CustomReferenceDot
                x={margin.left + scaleX(chartData.payerAllowed)}
                y={margin.top + 18}
                color="#FF7733"
                label="Payer Allowed"
                value={chartData.payerAllowed}
                chartWidth={chartWidth}
                marginLeft={margin.left}
                marginRight={margin.right}
                additionalLabel="Payer Adjusted"
                additionalValue={String(Math.round((chartData.contractValue - chartData.payerAllowed) * 100) / 100)}
              />
            </>
          )}

        {/* All tooltips rendered here to ensure they appear on top */}
        {chartData.payerPaid > 0 && (
          <BarTooltip
            barX={payerPaidX}
            barY={barY}
            barWidth={payerPaidWidth}
            barHeight={barHeight}
            label="Payer Paid"
            value={chartData.payerPaid}
            chartWidth={chartWidth}
            marginLeft={margin.left}
            marginRight={margin.right}
            show={hoveredBar === 'payerPaid'}
            mouseX={mousePos?.x}
            mouseY={mousePos?.y}
          />
        )}
        {chartData.patientPaid > 0 && (
          <BarTooltip
            barX={patientPaidX}
            barY={barY}
            barWidth={patientPaidWidth}
            barHeight={barHeight}
            label="Patient Paid"
            value={chartData.patientPaid}
            chartWidth={chartWidth}
            marginLeft={margin.left}
            marginRight={margin.right}
            show={hoveredBar === 'patientPaid'}
            mouseX={mousePos?.x}
            mouseY={mousePos?.y}
          />
        )}
        {chartData.patientPending > 0 && (
          <BarTooltip
            barX={patientPendingX}
            barY={barY}
            barWidth={patientPendingWidth}
            barHeight={barHeight}
            label="Patient Pending"
            value={chartData.patientPending}
            chartWidth={chartWidth}
            marginLeft={margin.left}
            marginRight={margin.right}
            show={hoveredBar === 'patientPending'}
            mouseX={mousePos?.x}
            mouseY={mousePos?.y}
          />
        )}
        {chartData.charge > 0 && (() => {
          const usedValue =
            chartData.payerPaid +
            chartData.patientPaid +
            chartData.patientPending

          const remainingValue = Math.max(0, chartData.charge - usedValue)

          if (remainingValue <= 0) {
            return null
          }

          const remainingX =
            margin.left +
            scaleX(chartData.payerPaid + chartData.patientPaid + chartData.patientPending)
          const remainingWidth = scaleX(remainingValue)

          return (
            <BarTooltip
              barX={remainingX}
              barY={barY}
              barWidth={remainingWidth}
              barHeight={barHeight}
              label="Adjustments"
              value={remainingValue}
              chartWidth={chartWidth}
              marginLeft={margin.left}
              marginRight={margin.right}
              show={hoveredBar === 'adjustments'}
              mouseX={mousePos?.x}
              mouseY={mousePos?.y}
            />
          )
        })()}
        {chartData.payerAllowed !== null &&
          chartData.contractValue !== null &&
          chartData.payerAllowed < chartData.contractValue && (
            <BarTooltip
              barX={margin.left + scaleX(chartData.payerAllowed)}
              barY={barY}
              barWidth={scaleX(chartData.contractValue - chartData.payerAllowed)}
              barHeight={barHeight}
              label="Payer Adjusted"
              value={chartData.contractValue - chartData.payerAllowed}
              chartWidth={chartWidth}
              marginLeft={margin.left}
              marginRight={margin.right}
              show={hoveredBar === 'payerAdjustedGrey' || hoveredBar === 'payerAdjustedOverlay'}
              mouseX={mousePos?.x}
              mouseY={mousePos?.y}
            />
          )}
      </svg>
      <div style={{ paddingLeft: '12px', paddingBottom: '10px', marginTop: '-50px' }}>
        {renderCustomLegend(chartData)}
      </div>
    </div>
  )
}
